import React, { useRef, useState } from 'react';
import { FolderUp, Images, Loader2, X, Check, AlertCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';

const MAX_BYTES = 15 * 1024 * 1024;
const isImage = (f) => /^image\//.test(f.type) || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i.test(f.name);
const readFile = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
const natural = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

// Upload a whole folder (or many files) of photos straight into one album, in filename order.
export default function AlbumBulkUploader({ album, onClose, onDone }) {
  const { adminAlbumUpload, adminAlbumPhotos, adminAlbumReorder } = useAuth();
  const folderRef = useRef(null);
  const filesRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [shared, setShared] = useState(true);
  const [state, setState] = useState({}); // name -> 'ok' | 'error:<msg>' | 'busy'
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);

  const pick = (list) => {
    const arr = Array.from(list || []).filter(isImage).sort((a, b) => natural(a.webkitRelativePath || a.name, b.webkitRelativePath || b.name));
    const skipped = Array.from(list || []).length - arr.length;
    if (skipped) toast.info(`${skipped} non-image file${skipped !== 1 ? 's' : ''} skipped.`);
    setFiles(arr); setState({}); setDone(0);
  };

  const start = async () => {
    setRunning(true);
    let ok = 0; let n = 0;
    const newIds = new Array(files.length).fill(null);
    const queue = files.map((f, idx) => [f, idx]);
    const worker = async () => {
      while (queue.length) {
        const [f, idx] = queue.shift();
        const key = f.webkitRelativePath || f.name;
        if (f.size > MAX_BYTES) { setState((s) => ({ ...s, [key]: 'error:Larger than 15 MB' })); n += 1; setDone(n); continue; }
        setState((s) => ({ ...s, [key]: 'busy' }));
        try {
          const created = await adminAlbumUpload(album, { data: await readFile(f), content_type: f.type || 'image/jpeg', filename: f.name, shared });
          newIds[idx] = created.id;
          setState((s) => ({ ...s, [key]: 'ok' })); ok += 1;
        } catch (e) { setState((s) => ({ ...s, [key]: `error:${formatApiError(e, 'Upload failed')}` })); }
        n += 1; setDone(n);
      }
    };
    await Promise.all([worker(), worker()]);
    // Parallel uploads can land out of order: put the new photos after the existing ones, in file order.
    try {
      const added = newIds.filter(Boolean);
      if (added.length > 1) {
        const current = await adminAlbumPhotos(album);
        await adminAlbumReorder(album, [...current.map((p) => p.id).filter((id) => !added.includes(id)), ...added]);
      }
    } catch { /* order best-effort */ }
    setRunning(false);
    toast[ok === files.length ? 'success' : 'warning'](`${ok} of ${files.length} photo${files.length !== 1 ? 's' : ''} added to “${album}”.`);
    if (onDone) onDone(ok);
  };

  const finished = !running && done > 0 && done === files.length;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={running ? undefined : onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]" data-testid="album-bulk-uploader" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}><FolderUp size={17} /> Upload a folder into “{album}”</h3>
          <button onClick={onClose} disabled={running} data-testid="album-bulk-close" aria-label="Close"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          {!files.length && (
            <>
              <p className="text-sm" style={{ color: 'var(--ink)' }}>Choose a folder on your computer — every photo in it (and its sub-folders) is added to this album in filename order, titled from the file name. Dates and camera details are read from each photo.</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => folderRef.current && folderRef.current.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded font-title text-sm" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="album-bulk-pick-folder"><FolderUp size={15} /> Choose folder</button>
                <button onClick={() => filesRef.current && filesRef.current.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded font-title text-sm border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }} data-testid="album-bulk-pick-files"><Images size={15} /> Choose photos</button>
              </div>
              <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={(e) => pick(e.target.files)} data-testid="album-bulk-folder-input" />
              <input ref={filesRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => pick(e.target.files)} data-testid="album-bulk-files-input" />
            </>
          )}
          {files.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-title" style={{ color: 'var(--navy)' }} data-testid="album-bulk-count">{files.length} photo{files.length !== 1 ? 's' : ''} ready{done ? ` · ${done} done` : ''}</span>
                {!running && !done && <button onClick={() => setFiles([])} className="text-xs underline" data-testid="album-bulk-reset">choose again</button>}
              </div>
              {!done && <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} disabled={running} data-testid="album-bulk-shared" /> Visible to members straight away</label>}
              {running && <div className="h-2 rounded overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)' }}><div className="h-full transition-all" style={{ width: `${(done / files.length) * 100}%`, background: 'var(--maroon)' }} /></div>}
              <ul className="text-xs divide-y rounded border" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="album-bulk-list">
                {files.map((f) => { const key = f.webkitRelativePath || f.name; const st = state[key] || ''; return (
                  <li key={key} className="flex items-center gap-2 px-2 py-1.5" data-testid={`album-bulk-file-${f.name}`}>
                    <span className="truncate flex-1" title={key}>{key}</span>
                    <span className="text-[10px] tabular-nums" style={{ color: 'var(--olive)' }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    {st === 'busy' && <Loader2 size={13} className="animate-spin" style={{ color: 'var(--navy)' }} />}
                    {st === 'ok' && <Check size={13} style={{ color: '#1c6b2c' }} />}
                    {st.startsWith('error:') && <span className="inline-flex items-center gap-1" style={{ color: '#8a1f11' }} title={st.slice(6)}><AlertCircle size={13} /> {st.slice(6).slice(0, 40)}</span>}
                  </li>
                ); })}
              </ul>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          <button onClick={onClose} disabled={running} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }} data-testid="album-bulk-cancel">{finished ? 'Close' : 'Cancel'}</button>
          {!finished && <button onClick={start} disabled={running || !files.length} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-title disabled:opacity-50" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="album-bulk-start">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {running ? `Uploading ${done}/${files.length}…` : `Upload ${files.length || ''} photo${files.length !== 1 ? 's' : ''}`}
          </button>}
        </div>
      </div>
    </div>
  );
}
